import { IconChevronDown, IconPlugConnected } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
	type MarketingNavFeatured,
	type MarketingNavFeaturedIcon,
	type MarketingNavItem,
	MarketingNavigationItems,
	type MarketingNavLink,
	splitNavLinksIntoColumns,
} from "@/data/navigation";
import {
	type MarketingNavMatchable,
	marketingNavFeaturedCardBadgeClass,
	marketingNavFeaturedCardClassName,
	marketingNavFeaturedCardContentClass,
	marketingNavFeaturedCardDescriptionClass,
	marketingNavFeaturedCardTitleClass,
	marketingNavFeaturedPatternClass,
	marketingNavGroupActive,
	marketingNavItemActive,
	marketingNavLinkClassName,
	marketingNavMegaPanelClass,
	marketingNavMegaSimpleLinkClassName,
	marketingNavMobileGroupTriggerClassName,
	marketingNavMobileLinkClassName,
	marketingNavSiblingActive,
	marketingNavSubLinkClassName,
	marketingNavSubLinkDescriptionClass,
	marketingNavSubLinkTitleClass,
	marketingNavTrayClassName,
	marketingNavTriggerClassName,
} from "@/lib/marketing-nav-styles";
import { cn } from "@/lib/utils";

type MarketingHeaderNavProps = {
	pathname: string;
	variant?: "desktop" | "mobile";
	className?: string;
};

export function MarketingHeaderNav({
	pathname,
	variant = "desktop",
	className,
}: MarketingHeaderNavProps) {
	const isDesktop = variant === "desktop";

	return (
		<NavigationMenu
			align={isDesktop ? "center" : "start"}
			aria-label={isDesktop ? "Main navigation" : "Mobile navigation"}
			orientation={isDesktop ? "horizontal" : "vertical"}
			className={cn(
				isDesktop
					? "hidden justify-self-center lg:flex"
					: "w-full max-w-none flex-none",
				className,
			)}
		>
			<NavigationMenuList
				className={cn(
					isDesktop
						? cn(marketingNavTrayClassName, "flex-none")
						: "flex w-full flex-col items-stretch gap-1",
				)}
			>
				{MarketingNavigationItems.map((item) =>
					isDesktop ? (
						<DesktopMarketingNavItem
							key={item.name}
							item={item}
							pathname={pathname}
						/>
					) : (
						<MobileMarketingNavItem
							key={item.name}
							item={item}
							pathname={pathname}
						/>
					),
				)}
			</NavigationMenuList>
		</NavigationMenu>
	);
}

export function marketingNavMatchables(
	item: MarketingNavItem,
): MarketingNavMatchable[] {
	return [
		...(item.items ?? []).map((link) => ({
			href: link.href,
			isActive: link.isActive,
			contributesToGroupActive: link.contributesToGroupActive,
		})),
		...(item.featured ?? []).map((card) => ({
			href: card.href,
			isActive: card.isActive,
			contributesToGroupActive: card.contributesToGroupActive,
		})),
	];
}

function MarketingNavFeaturedIconSquare({
	icon,
}: {
	icon: MarketingNavFeaturedIcon;
}) {
	if (icon === "mcp") {
		return (
			<div
				className={cn(
					"relative z-1 flex size-12 shrink-0 items-center justify-center rounded-xl border border-border/60 bg-background/80 shadow-sm",
					"dark:border-white/15 dark:bg-white/10",
				)}
				aria-hidden
			>
				<IconPlugConnected className="size-6 text-primary" stroke={1.5} />
			</div>
		);
	}

	return null;
}

function DesktopMarketingNavItem({
	item,
	pathname,
}: {
	item: MarketingNavItem;
	pathname: string;
}) {
	if (item.items?.length || item.featured?.length) {
		return (
			<NavigationMenuItem className="shrink-0">
				<NavigationMenuTrigger
					className={marketingNavTriggerClassName(pathname, item)}
				>
					{item.name}
				</NavigationMenuTrigger>
				<NavigationMenuContent
					className={item.menuLayout === "mega" ? undefined : "w-full p-2"}
				>
					{item.menuLayout === "mega" ? (
						<MarketingNavMegaMenu item={item} pathname={pathname} />
					) : (
						<MarketingNavLegacyMenu item={item} pathname={pathname} />
					)}
				</NavigationMenuContent>
			</NavigationMenuItem>
		);
	}

	if (!item.href) {
		return null;
	}

	return (
		<NavigationMenuItem className="shrink-0">
			<NavigationMenuLink
				render={<Link to={item.href} />}
				active={marketingNavItemActive(pathname, item.href)}
				className={cn(
					marketingNavLinkClassName(pathname, item.href),
					"inline-flex w-max items-center justify-center",
				)}
			>
				{item.name}
			</NavigationMenuLink>
		</NavigationMenuItem>
	);
}

function MarketingNavMegaMenu({
	item,
	pathname,
}: {
	item: MarketingNavItem;
	pathname: string;
}) {
	const siblings = marketingNavMatchables(item);
	const linkColumns = splitNavLinksIntoColumns(
		item.items ?? [],
		item.linkColumns ?? 1,
	);

	return (
		<div
			className={cn(
				marketingNavMegaPanelClass,
				!item.featured?.length && "w-auto max-w-[min(100vw-2rem,18rem)]",
			)}
		>
			{linkColumns.length > 0 ? (
				<div className="flex min-w-0 flex-col justify-center gap-0 border-border/40 p-2 dark:border-white/10">
					{linkColumns.map((column) => (
						<ul
							key={column.map((link) => link.name).join("-")}
							className="min-w-[9.5rem] flex-1 space-y-0.5 px-1"
						>
							{column.map((link) => (
								<li key={link.href + link.name}>
									<MarketingNavSimpleLink
										link={link}
										pathname={pathname}
										siblings={siblings}
									/>
								</li>
							))}
						</ul>
					))}
				</div>
			) : null}

			{item.featured?.length ? (
				<div
					className={cn(
						"flex min-w-0 flex-1 flex-col justify-center gap-2 border-border/40 p-3",
						linkColumns.length > 0 && "border-l dark:border-white/10",
					)}
				>
					{item.featured.map((card) => (
						<MarketingNavFeaturedCard
							key={card.href + card.name}
							card={card}
							pathname={pathname}
							siblings={siblings}
						/>
					))}
				</div>
			) : null}
		</div>
	);
}

function MarketingNavLegacyMenu({
	item,
	pathname,
}: {
	item: MarketingNavItem;
	pathname: string;
}) {
	const siblings = marketingNavMatchables(item);

	return (
		<ul
			className={cn(
				item.menuLayout === "grid"
					? "grid w-full min-w-0 max-w-[min(100vw-2rem,28rem)] gap-1 sm:w-[32rem] sm:grid-cols-2"
					: "flex w-full min-w-[15rem] flex-col gap-1 sm:min-w-[16.5rem]",
			)}
		>
			{item.items?.map((link) => (
				<MarketingNavDetailedListItem
					key={link.href}
					link={link}
					pathname={pathname}
					siblings={siblings}
				/>
			))}
		</ul>
	);
}

function MarketingNavSimpleLink({
	link,
	pathname,
	siblings,
}: {
	link: MarketingNavLink;
	pathname: string;
	siblings: MarketingNavMatchable[];
}) {
	const matchable = { href: link.href, isActive: link.isActive };
	const className = marketingNavMegaSimpleLinkClassName(
		pathname,
		matchable,
		siblings,
	);
	const active = marketingNavSiblingActive(pathname, matchable, siblings);

	if (link.href === "#") {
		return (
			<span
				aria-disabled="true"
				className={cn(className, "cursor-default opacity-70")}
			>
				{link.name}
			</span>
		);
	}

	return (
		<NavigationMenuLink
			render={<Link to={link.href} />}
			active={active}
			className={className}
		>
			{link.name}
		</NavigationMenuLink>
	);
}

function MarketingNavFeaturedCard({
	card,
	pathname,
	siblings,
}: {
	card: MarketingNavFeatured;
	pathname: string;
	siblings: MarketingNavMatchable[];
}) {
	const matchable = { href: card.href, isActive: card.isActive };
	const className = marketingNavFeaturedCardClassName(
		pathname,
		matchable,
		siblings,
	);
	const active = marketingNavSiblingActive(pathname, matchable, siblings);
	const content = (
		<>
			<div
				className={marketingNavFeaturedPatternClass(card.pattern ?? "grid")}
				aria-hidden
			/>
			<div className={marketingNavFeaturedCardContentClass}>
				{card.icon ? <MarketingNavFeaturedIconSquare icon={card.icon} /> : null}
				<div className="flex min-w-0 flex-col gap-1">
					{card.badge ? (
						<div className={marketingNavFeaturedCardBadgeClass}>
							{card.badge}
						</div>
					) : null}
					<div className={marketingNavFeaturedCardTitleClass}>{card.name}</div>
					<div className={marketingNavFeaturedCardDescriptionClass}>
						{card.description}
					</div>
				</div>
			</div>
		</>
	);

	if (card.href === "#") {
		return (
			<div
				className={cn(className, "cursor-default opacity-80")}
				aria-disabled="true"
			>
				{content}
			</div>
		);
	}

	return (
		<NavigationMenuLink
			render={<Link to={card.href} />}
			active={active}
			className={className}
		>
			{content}
		</NavigationMenuLink>
	);
}

function MobileMarketingNavItem({
	item,
	pathname,
}: {
	item: MarketingNavItem;
	pathname: string;
}) {
	if (item.items?.length || item.featured?.length) {
		const open = marketingNavGroupActive(pathname, item);
		const siblings = marketingNavMatchables(item);

		return (
			<NavigationMenuItem className="w-full">
				<Collapsible defaultOpen={open} className="w-full">
					<CollapsibleTrigger
						className={marketingNavMobileGroupTriggerClassName(pathname, item)}
					>
						<span>{item.name}</span>
						<IconChevronDown className="size-4 shrink-0 opacity-60 transition-[transform,opacity] in-data-open:rotate-180 in-data-open:opacity-100" />
					</CollapsibleTrigger>
					<CollapsibleContent
						className={cn(
							"pt-0.5 pb-1.5 pl-1.5",
							(item.linkColumns ?? 1) > 1
								? "grid grid-cols-2 gap-1"
								: "flex flex-col gap-2",
						)}
					>
						{item.items?.map((link) => (
							<MobileNavSubLink
								key={link.href}
								link={link}
								pathname={pathname}
								siblings={siblings}
							/>
						))}
						{item.featured?.map((card) => (
							<MobileNavFeaturedCard
								key={card.href + card.name}
								card={card}
								pathname={pathname}
								siblings={siblings}
							/>
						))}
					</CollapsibleContent>
				</Collapsible>
			</NavigationMenuItem>
		);
	}

	if (!item.href) {
		return null;
	}

	return (
		<NavigationMenuItem className="w-full">
			<NavigationMenuLink
				render={<Link to={item.href} />}
				active={marketingNavItemActive(pathname, item.href)}
				className={marketingNavMobileLinkClassName(pathname, item.href)}
			>
				{item.name}
			</NavigationMenuLink>
		</NavigationMenuItem>
	);
}

function MobileNavSubLink({
	link,
	pathname,
	siblings,
}: {
	link: MarketingNavLink;
	pathname: string;
	siblings: MarketingNavMatchable[];
}) {
	const matchable = { href: link.href, isActive: link.isActive };
	const active = marketingNavSiblingActive(pathname, matchable, siblings);
	const className = cn(
		marketingNavMegaSimpleLinkClassName(pathname, matchable, siblings),
		"block w-full px-3 py-2.5",
	);

	if (link.href === "#") {
		return (
			<div className={cn(className, "cursor-default opacity-70")} aria-disabled>
				{link.name}
			</div>
		);
	}

	return (
		<NavigationMenuLink
			render={<Link to={link.href} />}
			active={active}
			className={className}
		>
			{link.name}
		</NavigationMenuLink>
	);
}

function MobileNavFeaturedCard({
	card,
	pathname,
	siblings,
}: {
	card: MarketingNavFeatured;
	pathname: string;
	siblings: MarketingNavMatchable[];
}) {
	const matchable = { href: card.href, isActive: card.isActive };
	const className = cn(
		marketingNavFeaturedCardClassName(pathname, matchable, siblings),
		"min-h-24 px-3 py-3",
	);
	const active = marketingNavSiblingActive(pathname, matchable, siblings);

	const inner = (
		<>
			<div
				className={marketingNavFeaturedPatternClass(card.pattern ?? "grid")}
				aria-hidden
			/>
			<div className={marketingNavFeaturedCardContentClass}>
				{card.icon ? <MarketingNavFeaturedIconSquare icon={card.icon} /> : null}
				<div className="flex min-w-0 flex-col gap-1">
					{card.badge ? (
						<div className={marketingNavFeaturedCardBadgeClass}>
							{card.badge}
						</div>
					) : null}
					<div className={marketingNavFeaturedCardTitleClass}>{card.name}</div>
					<div className={marketingNavFeaturedCardDescriptionClass}>
						{card.description}
					</div>
				</div>
			</div>
		</>
	);

	if (card.href === "#") {
		return (
			<div className={cn(className, "cursor-default opacity-80")} aria-disabled>
				{inner}
			</div>
		);
	}

	return (
		<NavigationMenuLink
			render={<Link to={card.href} />}
			active={active}
			className={className}
		>
			{inner}
		</NavigationMenuLink>
	);
}

function MarketingNavDetailedListItem({
	link,
	pathname,
	siblings,
}: {
	link: MarketingNavLink;
	pathname: string;
	siblings: MarketingNavMatchable[];
}) {
	const matchable = { href: link.href, isActive: link.isActive };

	return (
		<li>
			<NavigationMenuLink
				render={<Link to={link.href} />}
				active={marketingNavSiblingActive(pathname, matchable, siblings)}
				className={marketingNavSubLinkClassName(pathname, matchable, siblings)}
			>
				<div className="flex flex-col gap-0.5">
					<div className={marketingNavSubLinkTitleClass}>{link.name}</div>
					{link.description ? (
						<div className={marketingNavSubLinkDescriptionClass}>
							{link.description}
						</div>
					) : null}
				</div>
			</NavigationMenuLink>
		</li>
	);
}
